import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { NotificationsService } from '@/api'
import type { NotificationOut } from '@/api'
import type { NotificationStreamEvent } from '@/composables/useNotificationsStream'

export const useNotificationsStore = defineStore('notifications', () => {
  const notifications = ref<NotificationOut[]>([])
  const loading = ref(false)

  const unreadCount = computed(() => notifications.value.filter((n) => n.read_at == null).length)

  async function fetchNotifications(): Promise<void> {
    // Kept for callers that want an explicit refresh (and for tests).
    // The live UI is driven by the SSE stream — see useNotificationsStream.
    loading.value = true
    try {
      const result = await NotificationsService.listNotificationsNotificationsGet()
      notifications.value = result
    } catch {
      // notification failures are silent — the bell is a secondary UI element
    } finally {
      loading.value = false
    }
  }

  async function markRead(id: string): Promise<void> {
    try {
      await NotificationsService.markReadNotificationsIdReadPost({ id })
      // Local state updates arrive via the SSE `notification_updated`
      // event published by the backend — same path as cross-tab updates.
    } catch {
      // silent
    }
  }

  async function markAllRead(): Promise<void> {
    try {
      await NotificationsService.markAllReadNotificationsReadAllPost()
      // Local state updates arrive via the SSE `notifications_all_read`
      // event published by the backend.
    } catch {
      // silent
    }
  }

  function consumeEvent(event: NotificationStreamEvent): void {
    switch (event.type) {
      case 'bootstrap':
        notifications.value = event.notifications
        return
      case 'notification_created': {
        const existing = notifications.value.findIndex((n) => n.id === event.notification.id)
        if (existing === -1) {
          notifications.value = [event.notification, ...notifications.value]
        } else {
          notifications.value = [
            ...notifications.value.slice(0, existing),
            event.notification,
            ...notifications.value.slice(existing + 1),
          ]
        }
        return
      }
      case 'notification_updated': {
        const index = notifications.value.findIndex((n) => n.id === event.notification.id)
        if (index !== -1) {
          notifications.value = [
            ...notifications.value.slice(0, index),
            event.notification,
            ...notifications.value.slice(index + 1),
          ]
        }
        return
      }
      case 'notifications_all_read':
        notifications.value = notifications.value.map((n) =>
          n.read_at == null ? { ...n, read_at: event.read_at } : n,
        )
        return
    }
  }

  function $reset(): void {
    notifications.value = []
    loading.value = false
  }

  return {
    notifications,
    // Pinia setup stores must return state refs for $subscribe/devtools tracking.
    // fallow-ignore-next-line unused-store-members
    loading,
    unreadCount,
    fetchNotifications,
    markRead,
    markAllRead,
    consumeEvent,
    $reset,
  }
})
